import React from 'react';
import WaveSurfer from 'wavesurfer.js';
import { apiFetch } from '../utils/auth';
import s from './Chats.module.css';

const PROGRESS_ANIM_MS = 220;
const PROGRESS_ANIM_EASING = 'cubic-bezier(0.33, 0, 0.2, 1)';
const IsIOS = typeof navigator !== 'undefined'
    && (/iPad|iPhone|iPod/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

function formatTime(seconds) {
    if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
}

function applyProgressTransition(host, durationMs) {
    const shadow = host?.shadowRoot;
    if (!shadow) return;
    const dur = `${durationMs}ms`;
    const easing = PROGRESS_ANIM_EASING;
    const progress = shadow.querySelector('.progress');
    const canvases = shadow.querySelector('.canvases');
    const cursor = shadow.querySelector('.cursor');
    if (progress) {
        progress.style.transition = durationMs > 0
            ? `width ${dur} ${easing}`
            : 'none';
    }
    if (canvases) {
        canvases.style.transition = durationMs > 0
            ? `clip-path ${dur} ${easing}`
            : 'none';
    }
    if (cursor) {
        cursor.style.transition = durationMs > 0
            ? `left ${dur} ${easing}`
            : 'none';
    }
}

const AudioWaveform = React.forwardRef(function AudioWaveform(
    {
        src,
        filename,
        variant = 'full',
        onPlayingChange,
        onReady,
    },
    ref,
) {
    const isInline = variant === 'inline';
    const containerRef = React.useRef(null);
    const wavesurferRef = React.useRef(null);
    const blobRef = React.useRef(null);
    const isDraggingRef = React.useRef(false);
    const isPlayingRef = React.useRef(false);
    const seekAnimTimerRef = React.useRef(null);
    const [playing, setPlaying] = React.useState(false);
    const [loadError, setLoadError] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [muted, setMuted] = React.useState(false);

    const setPlayingState = React.useCallback((next) => {
        isPlayingRef.current = next;
        setPlaying(next);
        onPlayingChange?.(next);
    }, [onPlayingChange]);

    const syncProgressTransition = React.useCallback(() => {
        const host = containerRef.current;
        if (!host) return;
        if (isDraggingRef.current) {
            applyProgressTransition(host, 0);
            return;
        }
        if (isPlayingRef.current) {
            applyProgressTransition(host, 0);
            return;
        }
        applyProgressTransition(host, PROGRESS_ANIM_MS);
    }, []);

    const armSeekAnimation = React.useCallback(() => {
        const host = containerRef.current;
        if (!host) return;
        applyProgressTransition(host, PROGRESS_ANIM_MS);
        if (seekAnimTimerRef.current) clearTimeout(seekAnimTimerRef.current);
        seekAnimTimerRef.current = setTimeout(() => {
            seekAnimTimerRef.current = null;
            if (!isDraggingRef.current && !isPlayingRef.current) {
                applyProgressTransition(host, PROGRESS_ANIM_MS);
            }
        }, PROGRESS_ANIM_MS + 40);
    }, []);

    const bindWaveEvents = React.useCallback((ws) => {
        ws.on('ready', () => {
            const d = ws.getDuration();
            if (Number.isFinite(d)) setDuration(d);
            syncProgressTransition();
            onReady?.();
        });
        ws.on('play', () => {
            setPlayingState(true);
            syncProgressTransition();
        });
        ws.on('pause', () => {
            setPlayingState(false);
            syncProgressTransition();
        });
        ws.on('finish', () => {
            setPlayingState(false);
            syncProgressTransition();
        });
        ws.on('timeupdate', (t) => {
            if (Number.isFinite(t)) setCurrentTime(t);
            const d = ws.getDuration();
            if (Number.isFinite(d)) setDuration(d);
        });
        ws.on('seeking', (t) => {
            if (Number.isFinite(t)) setCurrentTime(t);
        });
        ws.on('interaction', (t) => {
            if (Number.isFinite(t)) setCurrentTime(t);
            if (!isDraggingRef.current) armSeekAnimation();
        });
        ws.on('dragstart', () => {
            isDraggingRef.current = true;
            syncProgressTransition();
        });
        ws.on('drag', (relX) => {
            const d = ws.getDuration();
            if (Number.isFinite(d) && Number.isFinite(relX)) {
                setCurrentTime(Math.max(0, Math.min(d, relX * d)));
            }
        });
        ws.on('dragend', () => {
            isDraggingRef.current = false;
            const t = ws.getCurrentTime();
            if (Number.isFinite(t)) setCurrentTime(t);
            syncProgressTransition();
        });
    }, [armSeekAnimation, onReady, setPlayingState, syncProgressTransition]);

    React.useImperativeHandle(ref, () => ({
        playPause: () => {
            wavesurferRef.current?.playPause();
        },
        play: () => {
            wavesurferRef.current?.play();
        },
        pause: () => {
            wavesurferRef.current?.pause();
        },
        isPlaying: () => isPlayingRef.current,
    }), []);

    React.useEffect(() => {
        if (!containerRef.current || !src) return;

        const el = containerRef.current;
        let cancelled = false;
        let timeoutId = null;
        let blobUrl = null;
        let tickId = null;

        const init = (audioUrl) => {
            if (cancelled || !el.isConnected) return;
            if (el.offsetWidth === 0) return;
            const ws = WaveSurfer.create({
                container: el,
                barWidth: 2,
                barRadius: 2,
                barGap: isInline ? 2 : 3,
                barMinHeight: isInline ? 14 : 20,
                cursorWidth: 0,
                height: isInline ? 26 : 30,
                responsive: true,
                progressColor: '#7E0097',
                waveColor: isInline ? '#d4b8e8' : 'white',
                cursorColor: 'transparent',
                ...(IsIOS ? {} : { backend: 'WebAudio' }),
                url: audioUrl,
                normalize: true,
                interact: true,
                dragToSeek: true,
                autoScroll: false,
                autoCenter: false,
            });
            if (cancelled) {
                ws.destroy();
                return;
            }
            wavesurferRef.current = ws;
            bindWaveEvents(ws);
            tickId = setInterval(() => {
                if (!wavesurferRef.current || cancelled) return;
                if (isDraggingRef.current) return;
                setCurrentTime(wavesurferRef.current.getCurrentTime());
                const d = wavesurferRef.current.getDuration();
                if (Number.isFinite(d)) setDuration(d);
            }, 250);
        };

        const tryInit = (audioUrl, attempt = 0) => {
            if (cancelled) return;
            requestAnimationFrame(() => {
                if (cancelled) return;
                init(audioUrl);
                if (!wavesurferRef.current && attempt < 10) {
                    timeoutId = setTimeout(() => tryInit(audioUrl, attempt + 1), 80);
                }
            });
        };

        setLoadError(false);
        setCurrentTime(0);
        setDuration(0);
        isDraggingRef.current = false;
        isPlayingRef.current = false;
        apiFetch(src)
            .then((r) => {
                if (!r.ok) throw new Error(String(r.status));
                return r.blob();
            })
            .then((blob) => {
                if (cancelled) return;
                blobRef.current = blob;
                blobUrl = URL.createObjectURL(blob);
                tryInit(blobUrl);
            })
            .catch(() => {
                if (!cancelled) setLoadError(true);
            });

        return () => {
            cancelled = true;
            if (tickId) clearInterval(tickId);
            if (timeoutId) clearTimeout(timeoutId);
            if (seekAnimTimerRef.current) clearTimeout(seekAnimTimerRef.current);
            if (wavesurferRef.current) {
                wavesurferRef.current.destroy();
                wavesurferRef.current = null;
            }
            if (blobUrl) URL.revokeObjectURL(blobUrl);
            blobRef.current = null;
        };
    }, [src, bindWaveEvents, isInline]);

    const togglePlay = (e) => {
        e.stopPropagation();
        wavesurferRef.current?.playPause();
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        const ws = wavesurferRef.current;
        if (!ws) return;
        const media = (typeof ws.getMediaElement === 'function' && ws.getMediaElement()) ||
            containerRef.current?.querySelector?.('audio');
        if (media) {
            media.muted = !muted;
            setMuted((m) => !m);
        } else if (typeof ws.setVolume === 'function') {
            setMuted((m) => {
                ws.setVolume(m ? 1 : 0);
                return !m;
            });
        }
    };

    const handleDownload = (e) => {
        e.stopPropagation();
        const blob = blobRef.current;
        if (!blob) return;
        const ext = blob.type === 'audio/mp4' ? '.m4a' : blob.type === 'audio/ogg' ? '.ogg' : '.webm';
        const name = (filename && !filename.includes('.')) ? filename + ext : (filename || 'audio' + ext);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loadError) {
        if (isInline) {
            return (
                <span className={s.audioWaveInlineError}>Не удалось загрузить</span>
            );
        }
        return (
            <div className={s.audioWave}>
                <span style={{ fontSize: 13, color: 'var(--c-muted, #6b7280)' }}>Не удалось загрузить аудио</span>
            </div>
        );
    }

    if (isInline) {
        return (
            <div className={s.audioWaveInline} onClick={(e) => e.stopPropagation()}>
                <div ref={containerRef} className={s.audioWaveInlineCanvas} />
                <span className={s.audioWaveInlineTime}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                </span>
            </div>
        );
    }

    return (
        <div className={s.audioWave}>
            <div className={s.audioWaveMain}>
                <button
                    type="button"
                    className={s.audioWavePlayBtn}
                    onClick={togglePlay}
                    aria-label={playing ? 'Пауза' : 'Воспроизвести'}
                >
                    {playing ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 5v14M16 5v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 5v14l11-7L8 5z" fill="currentColor" />
                        </svg>
                    )}
                </button>
                <div ref={containerRef} className={s.audioWaveCanvas} />
            </div>
            <div className={s.audioWaveBar}>
                <span className={s.audioWaveTime}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                <div className={s.audioWaveActions}>
                    <button
                        type="button"
                        className={s.audioWaveIconBtn}
                        onClick={toggleMute}
                        aria-label={muted ? 'Включить звук' : 'Выключить звук'}
                        title={muted ? 'Включить звук' : 'Выключить звук'}
                    >
                        {muted ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M22 8.99993L16 14.9999M16 8.99993L22 14.9999M9.63432 4.36561L6.46863 7.5313C6.29568 7.70425 6.2092 7.79073 6.10828 7.85257C6.01881 7.9074 5.92127 7.9478 5.81923 7.9723C5.70414 7.99993 5.58185 7.99993 5.33726 7.99993H3.6C3.03995 7.99993 2.75992 7.99993 2.54601 8.10892C2.35785 8.20479 2.20487 8.35777 2.10899 8.54594C2 8.75985 2 9.03987 2 9.59993V14.3999C2 14.96 2 15.24 2.10899 15.4539C2.20487 15.6421 2.35785 15.7951 2.54601 15.8909C2.75992 15.9999 3.03995 15.9999 3.6 15.9999H5.33726C5.58185 15.9999 5.70414 15.9999 5.81923 16.0276C5.92127 16.0521 6.01881 16.0925 6.10828 16.1473C6.2092 16.2091 6.29568 16.2956 6.46863 16.4686L9.63431 19.6342C10.0627 20.0626 10.2769 20.2768 10.4608 20.2913C10.6203 20.3038 10.7763 20.2392 10.8802 20.1175C11 19.9773 11 19.6744 11 19.0686V4.9313C11 4.32548 11 4.02257 10.8802 3.88231C10.7763 3.76061 10.6203 3.69602 10.4608 3.70858C10.2769 3.72305 10.0627 3.93724 9.63432 4.36561Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        ) : (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19.7479 4.99993C21.1652 6.97016 22 9.38756 22 11.9999C22 14.6123 21.1652 17.0297 19.7479 18.9999M15.7453 7.99993C16.5362 9.13376 17 10.5127 17 11.9999C17 13.4872 16.5362 14.8661 15.7453 15.9999M9.63432 4.36561L6.46863 7.5313C6.29568 7.70425 6.2092 7.79073 6.10828 7.85257C6.01881 7.9074 5.92127 7.9478 5.81923 7.9723C5.70414 7.99993 5.58185 7.99993 5.33726 7.99993H3.6C3.03995 7.99993 2.75992 7.99993 2.54601 8.10892C2.35785 8.20479 2.20487 8.35777 2.10899 8.54594C2 8.75985 2 9.03987 2 9.59993V14.3999C2 14.96 2 15.24 2.10899 15.4539C2.20487 15.6421 2.35785 15.7951 2.54601 15.8909C2.75992 15.9999 3.03995 15.9999 3.6 15.9999H5.33726C5.58185 15.9999 5.70414 15.9999 5.81923 16.0276C5.92127 16.0521 6.01881 16.0925 6.10828 16.1473C6.2092 16.2091 6.29568 16.2956 6.46863 16.4686L9.63431 19.6342C10.0627 20.0626 10.2769 20.2768 10.4608 20.2913C10.6203 20.3038 10.7763 20.2392 10.8802 20.1175C11 19.9773 11 19.6744 11 19.0686V4.9313C11 4.32548 11 4.02257 10.8802 3.88231C10.7763 3.76061 10.6203 3.69602 10.4608 3.70858C10.2769 3.72305 10.0627 3.93724 9.63432 4.36561Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        )}
                    </button>
                    <button
                        type="button"
                        className={s.audioWaveIconBtn}
                        onClick={handleDownload}
                        aria-label="Скачать"
                        title="Скачать"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
});

export default AudioWaveform;
